import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen,
  BookMarked,
  CheckSquare,
  BarChart3,
  Users,
  LogOut,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { logout as logoutRequest, updateMyAvatar } from "../api/auth";
import { uploadImage } from "../api/files";
import { fileUrl } from "../api/files";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ROLE_LABELS, type AcademyRole } from "../types/user";
import { MyCoursesSection } from "../components/MyCoursesSection";
import { StudentDashboard } from "../components/StudentDashboard";

const STAFF_ROLES: AcademyRole[] = [
  "super_admin",
  "admin",
  "content_manager",
  "curator",
  "mentor",
];

const ROLES_THAT_SEE_ANALYTICS: AcademyRole[] = [
  "super_admin",
  "admin",
  "curator",
  "mentor",
  "content_manager",
];

const ROLES_THAT_SEE_USERS: AcademyRole[] = [
  "super_admin",
  "admin",
  "content_manager",
  "curator",
  "mentor",
];

interface Tile {
  to: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  visible: boolean;
}

export function DashboardPage() {
  const { user, setUser } = useCurrentUser();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  if (!user) {
    return null;
  }

  // Не-staff (trainee/guide/manager) видят mobile-first дашборд по мокапу #1.
  const isStaff = user.roles.some((r) => STAFF_ROLES.includes(r));
  if (!isStaff) {
    return <StudentDashboard />;
  }

  async function handleLogout() {
    await logoutRequest();
    setUser(null);
    navigate("/login", { replace: true });
  }

  async function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAvatar(true);
    try {
      const result = await uploadImage(f);
      const updated = await updateMyAvatar(result.id);
      setUser(updated);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const tiles: Tile[] = [
    {
      to: "/courses",
      title: "Курсы",
      subtitle: "Конструктор курсов, модулей и уроков. Markdown-редактор.",
      icon: BookOpen,
      visible: true,
    },
    {
      to: "/quizzes",
      title: "Тесты",
      subtitle: "4 типа вопросов, авто-проверка, breakdown по политике.",
      icon: CheckSquare,
      visible: true,
    },
    {
      to: "/kb",
      title: "База знаний",
      subtitle: "Регламенты, скрипты, частые вопросы. FTS-поиск.",
      icon: BookMarked,
      visible: true,
    },
    {
      to: "/users",
      title: "Пользователи",
      subtitle: "Создавать гидов и стажёров, выдавать роли, сбрасывать пароли.",
      icon: Users,
      visible: user.roles.some((r) => ROLES_THAT_SEE_USERS.includes(r)),
    },
    {
      to: "/analytics",
      title: "Аналитика",
      subtitle: "Прогресс учеников, риск-список, готовые к допуску.",
      icon: BarChart3,
      visible: user.roles.some((r) => ROLES_THAT_SEE_ANALYTICS.includes(r)),
    },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Петрополь Академия</h1>
          <p className="mt-1 text-sm text-ink-muted">Привет, {user.full_name}.</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-surface-alt"
        >
          <LogOut size={14} />
          Выйти
        </button>
      </header>

      <section className="card p-6">
        <div className="mb-4 flex items-center gap-4">
          {user.avatar_file_id ? (
            <img
              src={fileUrl(user.avatar_file_id)}
              alt=""
              className="h-16 w-16 rounded-full object-cover ring-2 ring-line"
            />
          ) : (
            <div className="grid h-16 w-16 place-items-center rounded-full bg-brand/10 text-xl font-bold uppercase text-brand">
              {user.full_name.charAt(0)}
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              onChange={handleAvatarPick}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Upload size={14} />
              {uploadingAvatar
                ? "Загружаем…"
                : user.avatar_file_id
                  ? "Сменить аватар"
                  : "Загрузить аватар"}
            </button>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-muted">Логин</div>
            <div className="mt-0.5 text-sm font-medium text-ink">{user.login}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-muted">Роли</div>
            <div className="flex flex-wrap gap-2 pt-1">
              {user.roles.map((r) => (
                <span key={r} className="badge badge-brand">
                  {ROLE_LABELS[r] ?? r}
                </span>
              ))}
            </div>
          </div>
          {user.telegram_user_id !== null && (
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-muted">Telegram</div>
              <div className="mt-0.5 text-sm text-ink">{user.telegram_user_id}</div>
            </div>
          )}
        </div>
      </section>

      <section className="mt-6 grid gap-3 sm:grid-cols-2">
        {tiles.filter((t) => t.visible).map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              className="group card flex items-start gap-3 p-4 transition hover:-translate-y-0.5 hover:ring-brand/40 hover:shadow-md"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand transition group-hover:bg-brand group-hover:text-white">
                <Icon size={18} strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <div className="text-base font-semibold text-ink group-hover:text-brand">
                  {t.title}
                </div>
                <div className="mt-1 text-sm text-ink-muted">{t.subtitle}</div>
              </div>
            </Link>
          );
        })}
      </section>

      <MyCoursesSection />
    </div>
  );
}
